import type { PropsWithChildren } from "react"
import { useEffect, useRef, useState } from "react"
import * as Sentry from "@sentry/react"
import { useLocation } from "@tanstack/react-router"

import type { AppErrorBoundary } from "@productlane/lib/src/error-report"
import { cn } from "@productlane/lib/src/browser"
import { shortErrorReference } from "@productlane/lib/src/error-report"
import { Button } from "@productlane/ui"

import { vanillaApi } from "@/lib/api"

type ReportState = "idle" | "sending" | "sent"

// Raw error text never renders here: it can carry schema or database internals.
// The Sentry event id is the only detail the reader sees and can send back.
export function ErrorFallback({
	eventId,
	boundary,
	size,
	retryFailed,
	onRetry,
}: {
	eventId?: string
	boundary: AppErrorBoundary
	size: "page" | "compact"
	retryFailed: boolean
	onRetry: () => void
}) {
	const [report, setReport] = useState<ReportState>("idle")
	const reference = shortErrorReference({ eventId })

	const sendReport = async () => {
		setReport("sending")
		const pathname = window.location.pathname
		Sentry.captureFeedback(
			{ message: "Dashboard error report", associatedEventId: eventId },
			{ captureContext: { tags: { boundary, route: pathname } } },
		)
		try {
			await vanillaApi.workspace.reportAppError.mutate({
				eventId,
				boundary,
				pathname,
				userAgent: navigator.userAgent,
			})
		} catch {
			// Sentry already holds the report; a signed-out session cannot create a thread.
		}
		setReport("sent")
	}

	const buttonSize = size === "page" ? "lg" : "default"
	const retry = retryFailed ? () => window.location.reload() : onRetry

	return (
		<div
			role="alert"
			className={cn(
				"flex w-full justify-center",
				size === "page" ? "px-6 pb-24 pt-[18dvh]" : "px-4 py-6",
				// The root boundary replaces the whole app, so it fills the screen itself.
				boundary === "root" && "min-h-dvh bg-white dark:bg-gray-950",
			)}
		>
			<div
				className={cn(
					"flex flex-col items-start text-left",
					size === "page" ? "max-w-[400px]" : "max-w-sm",
				)}
			>
				<div
					className={cn(
						"font-semibold tracking-tight text-gray-900 dark:text-gray-50",
						size === "page" ? "text-xl" : "text-sm",
					)}
				>
					Something went wrong
				</div>
				<p
					className={cn(
						"text-gray-500 dark:text-gray-400",
						size === "page" ? "mt-2 text-[15px] leading-6" : "mt-1 text-sm",
					)}
				>
					This page didn&apos;t load. Try again, or report it and we&apos;ll
					look into it.
				</p>
				<div
					className={cn(
						"flex items-center gap-2",
						size === "page" ? "mt-6" : "mt-4",
					)}
				>
					<Button size={buttonSize} isRounded onClick={retry}>
						{retryFailed ? "Reload page" : "Try again"}
					</Button>
					<Button
						variant="outline"
						size={buttonSize}
						isRounded
						disabled={report !== "idle"}
						isLoading={report === "sending"}
						onClick={() => void sendReport()}
					>
						Report
					</Button>
				</div>
				<div className="mt-4 h-5 self-stretch text-center text-xs text-gray-500 dark:text-gray-400">
					{report === "sent" ? (
						"Thanks, we got your report."
					) : reference ? (
						<span className="select-all font-mono">{reference}</span>
					) : null}
				</div>
			</div>
		</div>
	)
}

// Mounted alongside the fallback so leaving the route that threw clears the
// error. Without it a single bad screen keeps the fallback up until a reload.
function ResetOnNavigate({ onReset }: { onReset: () => void }) {
	const { pathname } = useLocation()
	const erroredAt = useRef(pathname)
	useEffect(() => {
		if (pathname !== erroredAt.current) onReset()
	}, [pathname, onReset])
	return null
}

interface ErrorBoundaryProps {
	boundary: AppErrorBoundary
	size?: "page" | "compact"
	// Off by default because the root boundary in main.tsx sits outside the router.
	resetOnNavigate?: boolean
}

export function ErrorBoundary({
	children,
	boundary,
	size = "page",
	resetOnNavigate = false,
}: PropsWithChildren<ErrorBoundaryProps>) {
	// A reset re-runs the same query, so a second failure in a row offers a reload.
	const [failures, setFailures] = useState(0)

	return (
		<Sentry.ErrorBoundary
			beforeCapture={(scope) => {
				scope.setTag("boundary", boundary)
				scope.setTag("route", window.location.pathname)
			}}
			onError={() => setFailures((count) => count + 1)}
			fallback={({ eventId, resetError }) => (
				<>
					{resetOnNavigate && (
						<ResetOnNavigate
							onReset={() => {
								setFailures(0)
								resetError()
							}}
						/>
					)}
					<ErrorFallback
						eventId={eventId || undefined}
						boundary={boundary}
						size={size}
						retryFailed={failures >= 2}
						onRetry={resetError}
					/>
				</>
			)}
		>
			{children}
		</Sentry.ErrorBoundary>
	)
}
